import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useI18n } from '../../../provider/i18n'
import {
  MdDelete,
  MdEdit,
  MdKeyboardArrowRight,
  MdOutlineChatBubbleOutline,
} from 'react-icons/md'
import { Blue300, Blue600, Red800 } from '../../../lib/colors'
import * as Time from '../../../lib/time'
import useBasketData from '../../../hooks/useBasketData'
import { Link } from 'react-router-dom'
import { GraphQLError } from 'graphql'
import ProfileSelect from '../../../components/NewProfileSelect'
import { GetProfilesSubscription } from '../../../generated/urql.client'
import { laboratoryTestsToPriceString } from '../../../lib/test'
import {
  ConfirmDialog,
  ConfirmDialogType,
} from '../../../components/ConfirmDialog'
import { PromptModal, PromptModalType } from '../../../components/PromptModal'
import { AuthContext } from '../../../provider/auth'
import Services from './Services'
import { sortProfile } from '../../../lib/sort'
import { HomeCollectionForm } from './HomeCollectionForm'
import { StationaryCollectionForm } from './StationaryCollectionForm'

const Tests = () => {
  const i18n = useI18n()
  const confirmDialogRef = useRef<ConfirmDialogType>(null)
  const promptModalRef = useRef<PromptModalType>(null)
  const { isLogged } = useContext(AuthContext)
  const {
    orderCost,
    orderCostError,
    tests,
    address,
    form,
    changeItem,
    removeItem,
  } = useBasketData()
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  const collectionType = watch('collectionType') || 'home'

  const allTestsHaveNurseAvailable = useMemo(() => {
    if (tests.length === 0) return true
    return tests.every((item) => item.test.isNurseAvailable === true)
  }, [tests])

  // Automatycznie przełącz na "stationary", jeśli "home" nie jest dostępne
  useEffect(() => {
    if (collectionType === 'home' && !allTestsHaveNurseAvailable && tests.length > 0) {
      setValue('collectionType', 'stationary')
      setValue('collectionDate', '')
    }
  }, [allTestsHaveNurseAvailable, collectionType, setValue, tests.length])

  const doEditClientNotes = useCallback(() => {
    promptModalRef.current?.show({
      message: i18n.t('screens.basket.form.notes.prompt.message'),
      onSuccess: (value) => {
        setValue('clientNotes', value)
      },
    })
  }, [i18n, setValue])

  return (
    <div id="tests-section" className="flex flex-col gap-2">
      <span className="text-lg md:text-xl font-bold">
        {i18n.t('screens.basket.sections.tests.title')}
      </span>
      <ul className="flex flex-col gap-1">
        {tests.sort(sortProfile).map((item, i) => {
          const testCost = orderCost?.collections?.[0].tests.find(
            ({ id }: any) => id === item.test.id
          )

          const testOpeningHours = (
            orderCostError &&
            isOutsideOpeningHoursError(orderCostError?.graphQLErrors[0])
              ? orderCostError?.graphQLErrors[0].extensions.testsOpeningHours
              : []
          ).find(([testId]: any) => item.test.id === testId)

          const testIdsNotAvailableInLocation =
            orderCostError &&
            isTestsNotAvailableInLocationError(orderCostError?.graphQLErrors[0])
              ? orderCostError?.graphQLErrors[0].extensions.testIds
              : []

          const testIdsNotSupportedByCollectionPoint =
            orderCostError &&
            orderCostError?.graphQLErrors[0] &&
            isCollectionPointTestNotSupportedError(orderCostError?.graphQLErrors[0])
              ? orderCostError?.graphQLErrors[0].extensions.testIds
              : []

          return (
            <li
              key={`test-${item.test.id}-${i}`}
              className="border-b border-b-border border-opacity-20 p-4 bg-white flex flex-col md:flex-row gap-4 items-center rounded-lg min-h-16 justify-center"
            >
              <div className="flex gap-4 w-full justify-between">
                <div className="flex flex-col justify-center gap-2">
                  <div>
                    <p className="text-lg font-bold leading-tight">
                      <Link
                        to={`/test/${item.test.id}`}
                        style={{ textDecoration: 'none' }}
                      >
                        <span className="text-black">
                          {item.test.shortName || item.test.name}
                        </span>
                        <MdKeyboardArrowRight
                          size={20}
                          color={Blue600}
                          className="inline"
                        />
                      </Link>
                    </p>
                    {item.test.shortName &&
                      item.test.shortName !== item.test.name && (
                        <p className="text-blue-700 text-xs">
                          {item.test.name}
                        </p>
                      )}
                    {testOpeningHours?.[1] && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.form.orderTests.testOpeningHours'
                        )}{' '}
                        {Time.toShortFormat(testOpeningHours[1][0])}-
                        {Time.toShortFormat(testOpeningHours[1][1])}
                      </p>
                    )}
                    {testOpeningHours?.[1] === null && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.form.orderTests.noOpenLaboratories'
                        )}
                      </p>
                    )}
                    {testIdsNotAvailableInLocation?.includes(item.test.id) && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.form.orderTests.notAvailableInLocation'
                        )}
                      </p>
                    )}
                    {testIdsNotSupportedByCollectionPoint?.includes(item.test.id) && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.form.orderTests.notSupportedByCollectionPoint'
                        )}
                      </p>
                    )}
                    {isLogged && !item.profile && (
                      <p className="text-red-700 text-xs">
                        {i18n.t(
                          'screens.basket.form.orderTests.assignToProfile'
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-4">
                  {isLogged && (
                    <ProfileSelect
                      onChange={(profile) =>
                        changeItem(item, {
                          ...item,
                          profile,
                        })
                      }
                      value={
                        item.profile as GetProfilesSubscription['client_profile'][number]
                      }
                      className="hidden md:block"
                    />
                  )}
                  <div>
                    {testCost && (
                      <div className="min-w-10">
                        <p className="text-base font-bold text-blue-700 whitespace-nowrap">
                          {i18n.t('screens.basket.form.orderTests.cost', {
                            cost: Number(testCost.cost).toFixed(2),
                          })}
                        </p>
                        {testCost.costBeforeDiscount && (
                          <p className="text-red-700 line-through text-right font-bold">
                            {i18n.t(
                              'screens.basket.form.orderTests.costBeforeDiscount',
                              {
                                cost: Number(
                                  testCost.costBeforeDiscount
                                ).toFixed(2),
                              }
                            )}
                          </p>
                        )}
                      </div>
                    )}
                    {!testCost && item.test.availableLaboratoryTests && (
                      <p className="text-base font-bold text-blue-700 min-w-10 text-right whitespace-nowrap">
                        {i18n.t('screens.basket.form.orderTests.cost', {
                          cost: laboratoryTestsToPriceString(
                            item.test.availableLaboratoryTests
                          ),
                        })}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      confirmDialogRef.current?.show({
                        message: i18n.t(
                          'screens.basket.form.orderTests.remove.alert.title',
                          {
                            testName: item.test.shortName || item.test.name,
                          }
                        ),
                        onSuccess: () => removeItem(item),
                      })
                    }}
                  >
                    <MdDelete size={24} color={Red800} />
                  </button>
                </div>
              </div>
              {isLogged && (
                <div className="flex flex-col sm:flex-row gap-2 sm:w-full md:hidden">
                  <ProfileSelect
                    onChange={(profile) =>
                      changeItem(item, {
                        ...item,
                        profile,
                      })
                    }
                    value={
                      item.profile as GetProfilesSubscription['client_profile'][number]
                    }
                    className="block md:hidden"
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {isLogged && (
        <>
          <div className="bg-white rounded-lg border-b border-b-border border-opacity-20">
            <div className="p-4 flex gap-4 items-center border-b border-b-border border-opacity-20">
              <div className="flex flex-col gap-4 w-full">
                <label className="text-sm font-bold text-blue-700">
                  {i18n.t('screens.basket.form.collectionType.label')}
                </label>
                <div className="flex gap-4">
                  <label
                    className={`flex items-center gap-2 ${
                      allTestsHaveNurseAvailable
                        ? 'cursor-pointer'
                        : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <input
                      type="radio"
                      value="home"
                      checked={collectionType === 'home'}
                      disabled={!allTestsHaveNurseAvailable}
                      onChange={(e) => {
                        if (allTestsHaveNurseAvailable) {
                          setValue('collectionType', 'home')
                          setValue('collectionPointId', undefined)
                          setValue('collectionSlotId', undefined)
                          setValue('selectedCollectionPoint', undefined)
                          setValue('isVoucher', false)
                        }
                      }}
                    />
                    <span>
                      {i18n.t('screens.basket.form.collectionType.home')}
                    </span>
                  </label>
                  {!allTestsHaveNurseAvailable && collectionType === 'home' && (
                    <div className="text-red-700 text-xs">
                      {i18n.t(
                        'screens.basket.form.collectionType.homeNotAvailable'
                      )}
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="stationary"
                      checked={collectionType === 'stationary'}
                      onChange={(e) => {
                        setValue('collectionType', 'stationary')
                        setValue('collectionDate', '')
                      }}
                    />
                    <span>
                      {i18n.t('screens.basket.form.collectionType.stationary')}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {collectionType === 'home' && (
              <HomeCollectionForm form={form} address={address} />
            )}

            {collectionType === 'stationary' && (
              <StationaryCollectionForm form={form} />
            )}
            <div className="p-4 flex gap-4 items-center">
              <MdOutlineChatBubbleOutline size={24} color={Blue300} />
              <div className="flex-col flex md:flex-row md:justify-between w-full">
                <div className="flex flex-col justify-center text-left leading-tight text-sm text-blue-700">
                  <p className="font-bold">
                    {i18n.t('screens.basket.form.notes.label')}
                  </p>
                </div>
                <div className="flex justify-between gap-4">
                  <p className="text-lg font-bold">
                    {watch('clientNotes') ||
                      i18n.t('screens.basket.form.notes.empty')}
                  </p>
                  <button onClick={doEditClientNotes}>
                    <MdEdit size={24} color={Blue600} />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <Services />
        </>
      )}

      <PromptModal ref={promptModalRef} />
      <ConfirmDialog ref={confirmDialogRef} />
    </div>
  )
}

type TestOpeningHours = [number, Time.Interval | null]

const isOutsideOpeningHoursError = (
  error: GraphQLError
): error is GraphQLError & {
  extensions: {
    code: 'outside-opening-hours'
    testsOpeningHours: TestOpeningHours[]
  }
} => {
  return error.extensions?.code === 'outside-opening-hours'
}

const isTestsNotAvailableInLocationError = (
  error: GraphQLError
): error is GraphQLError & {
  extensions: {
    code: 'tests-not-available-in-location'
    testIds: number[]
  }
} => {
  return error.extensions?.code === 'tests-not-available-in-location'
}

const isCollectionPointTestNotSupportedError = (
  error: GraphQLError
): error is GraphQLError & {
  extensions: {
    code: 'collection-point-test-not-supported'
    testIds: number[]
  }
} => {
  return error.extensions?.code === 'collection-point-test-not-supported'
}

export default Tests
